
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver } from 'vee-validate';
import { Validate } from '@/types';
import { toErrorMessage } from '@/utils/errorHandling';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import SelectComponent from '@/components/shared/Select.vue';
import TagLocaleEditor from '@/components/menu/TagLocaleEditor.vue';
import TagFormFields from '@/components/menu/TagFormFields.vue';
import Toast from '@/utils/toast';

const namespace: string = 'bulkTagCreate';

@Component<BulkCreateTagModal>({
	components: {
		StandardModal,
		InfoBanner,
		SelectComponent,
		TagLocaleEditor,
		TagFormFields,
		ValidationObserver
	}
})
export default class BulkCreateTagModal extends Vue {
	@Prop({ type: Boolean, required: false, default: false }) private showModal!: boolean;
	@Prop({ type: Array, required: false, default: () => [] }) private locationIds!: string[];
	@Action('startJob', { namespace }) private startJob!: (payload: BulkCreateTagJobPayload) => Promise<void>;
	@Getter('getRestaurants', { namespace: 'auth' }) private restaurants!: Tenant[] | null;

	$refs!: { observer: Validate; };

	private loading: boolean = false;
	private selectedLocale: RestaurantLocale = {} as RestaurantLocale;
	private tagForm: TagForm = {
		name: '',
		icon: 'mdi-heart',
		colorHex: '#757575'
	};

	private get isDefaultLocale(): boolean {
		return !this.selectedLocale || !Object.keys(this.selectedLocale).length || this.selectedLocale.is_default;
	}

	/**
	 * Locales defined across the selected locations, deduped by code and sorted default-first. The tag
	 * is created in every one of them at once, so the picker offers the union rather than any single
	 * location's list.
	 *
	 * @return {RestaurantLocale[]}
	 */
	private get availableLocales(): RestaurantLocale[] {
		const selected: Set<string> = new Set(this.locationIds);
		const byCode: Map<string, RestaurantLocale> = new Map();

		(this.restaurants ?? [])
			.filter((location: Tenant) => selected.has(location.id))
			.forEach((location: Tenant) => {
				(location.locales ?? []).forEach((locale: RestaurantLocale) => {
					if (!byCode.has(locale.locale_short)) {
						byCode.set(locale.locale_short, locale);
					}
				});
			});

		return [...byCode.values()].sort((a, b) => Number(b.is_default) - Number(a.is_default));
	}

	private created(): void {
		if (this.availableLocales.length > 1) {
			this.selectedLocale = this.availableLocales.find((locale: RestaurantLocale) => locale.is_default) ?? this.availableLocales[0];
		}
	}

	/**
	 * The localized names the operator entered, with the empty placeholders dropped.
	 *
	 * @return {Localization}
	 */
	private filledLocalization(): Localization {
		const localization: Localization = {};

		Object.keys(this.tagForm.localization ?? {}).forEach((localeShort: string) => {
			const name: string | undefined = this.tagForm.localization?.[localeShort]?.name;
			if (name) {
				localization[localeShort] = { name };
			}
		});

		return localization;
	}

	/**
	 * Start the create-tag job and close straight away. Progress — and any locations that already
	 * held a tag with this name — are reported by the card on the page, not by this modal.
	 *
	 * @return {Promise<void>}
	 */
	private async createTag(): Promise<void> {
		const isValid: boolean = await this.$refs.observer.validate();
		if (!isValid) {
			return;
		}

		this.loading = true;

		const payload: BulkCreateTagJobPayload = {
			location_ids: [...this.locationIds],
			name: this.tagForm.name,
			color: this.tagForm.colorHex,
			icon: this.tagForm.icon
		};

		// Only send locales the operator actually filled in — TagLocaleEditor seeds an empty entry for
		// every locale the picker visits, and an empty name would overwrite nothing useful.
		const localization: Localization = this.filledLocalization();
		if (Object.keys(localization).length) {
			payload.localization = localization;
		}

		try {
			await this.startJob(payload);
			this.$emit('started');
		}
		catch (error) {
			Toast.error(toErrorMessage(error));
		}
		finally {
			this.loading = false;
		}
	}
}
